import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet } from "../utils/api";
import { routes } from "../utils/utils.routes";
import { PageSectionHeader } from "../components/subcomponents/PageSectionHeader";
import { FaSearch } from "react-icons/fa";

const SearchI = FaSearch as any;

export const Search: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [query, setQuery] = useState(q);
  const [results, setResults] = useState<any>({ contents: [], faculties: [], filieres: [] });
  const [loading, setLoading] = useState(false);

  const runSearch = (text: string) => {
    if (!text.trim()) {
      setResults({ contents: [], faculties: [], filieres: [] });
      return;
    }
    setLoading(true);
    apiGet("/search", { q: text }).then(setResults).finally(() => setLoading(false));
  };

  useEffect(() => {
    setQuery(q);
    runSearch(q);
  }, [q]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({ q: query.trim() });
  };

  const total = results.contents.length + results.faculties.length + results.filieres.length;

  return (
    <>
      {/* <BreadcrumpComponent
        imageCover={bigCover}
        title="Recherche"
        subtitle="Trouvez articles, événements, activités, facultés et filières en quelques secondes."
      /> */}

      <div className="site-search-page page-content padding-x is-layout-constrained">
        <PageSectionHeader
          eyebrow="Recherche"
          title="Que recherchez-vous ?"
          lead="Saisissez un mot-clé pour explorer le site : programmes, actualités, événements et plus encore."
          align="center"
        />

        <form className="site-search-page__form" data-testid="search-form" onSubmit={handleSubmit}>
          <label className="site-search-page__field">
            <span className="screen-reader-text">Terme de recherche</span>
            <input
              className="site-search-page__input"
              data-testid="search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Événement, faculté, filière, article…"
              autoComplete="off"
            />
            <button
              className="site-search-page__btn"
              type="submit"
              data-testid="search-button"
              disabled={loading}
            >
              <SearchI aria-hidden />
              {loading ? "Recherche…" : "Rechercher"}
            </button>
          </label>
        </form>

        {q && (
          <p className="site-search-page__status" role="status">
            {loading ? (
              "Recherche en cours…"
            ) : (
              <>
                <strong>{total}</strong> résultat{total !== 1 ? "s" : ""} pour{" "}
                <strong>«&nbsp;{q}&nbsp;»</strong>
              </>
            )}
          </p>
        )}

        {!q && !loading && (
          <p className="site-search-page__hint">
            Astuce : essayez le nom d&apos;une faculté, d&apos;une filière ou d&apos;un mot-clé d&apos;actualité.
          </p>
        )}

        {results.faculties.length > 0 && (
          <section className="site-search-page__section">
            <h3 className="site-search-page__section-title">
              Facultés <span className="site-search-page__count">({results.faculties.length})</span>
            </h3>
            <div className="site-search-page__grid">
              {results.faculties.map((f: any) => (
                <Link
                  key={f.id}
                  to={`${routes.FACULTIES}/${f.slug}`}
                  data-testid={`result-faculty-${f.slug}`}
                  className="site-search-page__card site-search-page__card--faculty"
                >
                  <strong>{f.name}</strong>
                  <span>{f.domaine}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {results.filieres.length > 0 && (
          <section className="site-search-page__section">
            <h3 className="site-search-page__section-title">
              Filières <span className="site-search-page__count">({results.filieres.length})</span>
            </h3>
            <div className="site-search-page__grid">
              {results.filieres.map((fi: any) => (
                <Link
                  key={fi.id}
                  to={`${routes.FACULTIES}/${fi.faculty?.slug}`}
                  data-testid={`result-filiere-${fi.slug}`}
                  className="site-search-page__card site-search-page__card--filiere"
                >
                  <strong>{fi.name}</strong>
                  <span>{fi.faculty?.name}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {results.contents.length > 0 && (
          <section className="site-search-page__section">
            <h3 className="site-search-page__section-title">
              Articles, événements et activités{" "}
              <span className="site-search-page__count">({results.contents.length})</span>
            </h3>
            <div className="site-search-page__content-list">
              {results.contents.map((c: any) => (
                <Link
                  key={c.id}
                  to={`${routes.ARTICLESDETAILS}/${c.slug}`}
                  data-testid={`result-content-${c.slug}`}
                  className="site-search-page__content-card"
                >
                  {c.coverImage && (
                    <img src={c.coverImage} alt="" className="site-search-page__content-thumb" />
                  )}
                  <div className="site-search-page__content-body">
                    <span className="site-search-page__badge">{c.type}</span>
                    <h4>{c.title}</h4>
                    <p>{(c.excerpt || "").slice(0, 160)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {q && !loading && total === 0 && (
          <p className="site-search-page__empty">Aucun résultat trouvé. Essayez avec d&apos;autres mots-clés.</p>
        )}
      </div>
    </>
  );
};
